import { defineStore } from 'pinia';
import { ref } from 'vue';

import type { GrammarPrimaryButtonType } from '@nora-monorepo/grammar-ui';

import { i18n as i18nSetup } from '#ui-shared/i18n/i18n';

interface ConfirmModalProps {
  headerText?: string;
  bodyText?: string;
  confirmButtonLabel?: string;
  cancelButtonLabel?: string;
  defaultButtonFocus?: 'cancel' | 'confirm';
  isOpen?: boolean;
  onConfirm: () => void;
  onCancel?: () => void;
  isDelete?: boolean;
}

export const useConfirmModalStore = defineStore('confirmModalStore', () => {
  const { t } = i18nSetup.global;

  const isOpen = ref(false);
  const headerText = ref<string>('');
  const bodyText = ref<string>('');
  const confirmButtonLabel = ref<string>(t('common.confirm'));
  const cancelButtonLabel = ref<string>(t('common.cancel'));
  const onConfirm = ref<() => void>(() => (isOpen.value = false));
  const onCancel = ref<() => void>(() => (isOpen.value = false));
  const defaultButtonFocus = ref<'cancel' | 'confirm'>('cancel');
  const confirmButtonType = ref<GrammarPrimaryButtonType>('default');

  const reset = (): void => {
    if (onCancel.value) {
      onCancel.value();
    }

    isOpen.value = false;
    headerText.value = '';
    bodyText.value = '';
    confirmButtonLabel.value = t('common.confirm');
    cancelButtonLabel.value = t('common.cancel');
    onConfirm.value = () => (isOpen.value = false);
    onCancel.value = () => (isOpen.value = false);
    defaultButtonFocus.value = 'cancel';
    confirmButtonType.value = 'default';
  };

  const closeModal = (): void => {
    isOpen.value = false;
    reset();
  };

  const handleConfirm = (): void => {
    if (onConfirm.value) {
      onConfirm.value();
    }

    closeModal();
  };

  function showConfirmModal(options: ConfirmModalProps): void {
    isOpen.value = true;
    headerText.value = options.headerText || '';
    bodyText.value = options.bodyText || '';

    if (options.cancelButtonLabel) {
      cancelButtonLabel.value = options.cancelButtonLabel;
    }

    onConfirm.value = options.onConfirm;
    onCancel.value = options.onCancel || (() => (isOpen.value = false));
    defaultButtonFocus.value = options.defaultButtonFocus || 'cancel';

    if (options.isDelete) {
      confirmButtonType.value = 'danger';
      confirmButtonLabel.value = t('common.delete');
    }

    if (options.confirmButtonLabel) {
      confirmButtonLabel.value = options.confirmButtonLabel;
    }
  }

  return {
    showConfirmModal,
    closeModal,
    handleConfirm,
    isOpen,
    headerText,
    bodyText,
    confirmButtonLabel,
    cancelButtonLabel,
    onConfirm,
    defaultButtonFocus,
    confirmButtonType,
  };
});
